import { useParams, Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ArrowLeft, PlayCircle, Clapperboard } from "lucide-react";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { MediaView, mediaWrapClass } from "@/components/site/MediaView";
import { FramedImage } from "@/components/site/FramedImage";
import { SmartGallery } from "@/components/site/SmartGallery";
import { ActionButtons } from "@/components/site/ActionButtons";
import { galleryCover, mediaItems } from "@/lib/media";

const INFO_LABELS = { year: "Année", format: "Format", genre: "Genre(s)", thematique: "Thématique(s)", role: "Notre rôle", partners: "Partenaires", location: "Lieu", duration: "Durée" };

// Affiche une valeur qui peut être une chaîne (ancien format) ou un tableau (nouveau multi-select).
const fmt = (v) => (Array.isArray(v) ? v.filter(Boolean).join(", ") : (v || ""));

const ProjectBody = () => {
  const { content } = useContent();
  const { slug } = useParams();
  const meta = content.realisationsMeta || {};
  const list = content.realisations || [];
  const item = list.find((p) => p.slug === slug && (p.status || "published") !== "hidden");
  const related = list.filter((p) => p.slug !== slug && (p.status || "published") === "published").slice(0, 3);
  const info = item?.info || {};
  const infoRows = Object.keys(INFO_LABELS).filter((k) => {
    const v = info[k];
    return Array.isArray(v) ? v.filter(Boolean).length > 0 : !!v;
  });

  return (
    <div className="App bg-[#050505] min-h-screen flex flex-col">
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <Navbar />
      <main className="flex-1 pt-32 md:pt-40 pb-24">
        {!item ? (
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
            <div className="text-[11px] tracking-[0.35em] uppercase text-electric mb-4">Erreur 404</div>
            <h1 className="font-serif text-5xl md:text-7xl text-white mb-8">Projet introuvable</h1>
            <Link to="/#realisations" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white rounded-full text-[13px] tracking-[0.12em] uppercase hover:border-electric hover:text-electric transition-colors"><ArrowLeft size={15} /> Toutes les réalisations</Link>
          </div>
        ) : (
          <article className="max-w-5xl mx-auto px-6 md:px-12" data-testid="project-detail">
            <Reveal>
              <Link to="/#realisations" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-secondary2 hover:text-electric transition-colors mb-8"><ArrowLeft size={14} /> Réalisations</Link>
              <div className="text-[11px] tracking-[0.3em] uppercase text-electric mb-4">{item.cat || meta.detailEyebrow || "Réalisation"}</div>
              <h1 data-testid="project-detail-title" className="font-serif text-4xl md:text-7xl text-white tracking-tight leading-[0.98] mb-8">{item.title}</h1>
            </Reveal>

            <Reveal className="mb-14">
              {(() => {
                // Main image + additional gallery photos → carousel INSIDE the same hero frame.
                // Each image keeps its OWN saved frame (X/Y/Zoom). Dynamic for every réalisation.
                const heroMedia = [
                  { type: "image", url: item.img || item.image, frame: item.detailFrame },
                  ...mediaItems(item.gallery),
                ].filter((m) => m.url || m.type === "video");
                const heroBox = mediaWrapClass(item.detailFrame);
                const heroWrap = heroBox.wrap;
                if (item.mediaType !== "video" && heroMedia.length > 1) {
                  return <div className={heroWrap}><SmartGallery images={heroMedia} label="" frameClass={heroBox.aspect || "aspect-video"} imgFit="object-cover"
                    options={{ mode: "slide", autoplay: true, intervalSec: 5, arrows: true, dots: true, loop: true, swipe: true, pauseOnHover: true }} /></div>;
                }
                return (
                  <div className={heroBox.cls}>
                    <MediaView item={{ ...item, image: item.img || item.image }} alt={item.title} frame={item.detailFrame} />
                  </div>
                );
              })()}
            </Reveal>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-12 mb-16">
              <div>
                {item.synopsis && <Reveal className="mb-10">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-3">{meta.synopsisLabel || "Synopsis"}</div>
                  <p className="text-lg md:text-xl text-white/90 font-light leading-relaxed">{item.synopsis}</p>
                </Reveal>}
                {item.description && <Reveal>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-3">{meta.aboutLabel || "À propos du projet"}</div>
                  <p className="text-base md:text-lg text-secondary2 font-light leading-relaxed whitespace-pre-line">{item.description}</p>
                </Reveal>}
              </div>
              {infoRows.length > 0 && (
                <Reveal delay={0.1}>
                  <div className="rounded-xl border border-white/10 bg-surface/50 p-6">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-4">{meta.infoLabel || "Fiche technique"}</div>
                    <dl className="space-y-3">
                      {infoRows.map((k) => (
                        <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-3">
                          <dt className="text-xs tracking-[0.15em] uppercase text-secondary2">{INFO_LABELS[k]}</dt>
                          <dd className="text-sm text-white text-right">{fmt(info[k])}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </Reveal>
              )}
            </div>

            {item.mediaType === "video" && (item.gallery || []).length > 0 && <Reveal className="mb-16"><SmartGallery images={item.gallery} options={item.gallerySettings} mode={item.gallerySettings ? undefined : "slide"} label={meta.galleryLabel || "Galerie"} /></Reveal>}

            {(item.filmUrl || item.makingOf) && (
              <Reveal className="mb-16 flex flex-wrap gap-3" data-testid="project-media-links">
                {item.filmUrl && <a href={item.filmUrl} target="_blank" rel="noreferrer" data-testid="project-film-link" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-electric text-black text-[12px] tracking-[0.12em] uppercase font-semibold hover:bg-white transition-colors"><PlayCircle size={16} /> Voir le film / la bande-annonce</a>}
                {item.makingOf && <a href={item.makingOf} target="_blank" rel="noreferrer" data-testid="project-makingof-link" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/20 text-white text-[12px] tracking-[0.12em] uppercase hover:border-electric hover:text-electric transition-colors"><Clapperboard size={16} /> Making-of</a>}
              </Reveal>
            )}

            {(item.actions || []).length > 0 && (
              <Reveal className="mb-16"><ActionButtons actions={item.actions} context={{ refId: item.id, refTitle: item.title }} /></Reveal>
            )}

            {related.length > 0 && (
              <div className="mt-24 border-t border-white/10 pt-12">
                <h2 className="font-serif text-3xl text-white mb-8">Autres réalisations</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {related.map((p, i) => (
                    <Link key={p.slug || i} to={`/projet/${p.slug}`} data-testid={`related-project-${i}`} className="group rounded-lg overflow-hidden border border-white/10 bg-surface/60 hover:border-electric/40 transition-colors">
                      <div className="h-44 overflow-hidden bg-[#0e0e0e]">{(p.img || galleryCover(p.gallery)) && <FramedImage src={p.img || galleryCover(p.gallery)} frame={p.imgFrame} alt={p.title} className="group-hover:scale-105 transition-transform duration-700" />}</div>
                      <div className="p-5"><div className="text-[10px] tracking-[0.2em] uppercase text-electric mb-2">{p.cat}</div><h3 className="font-serif text-lg text-white">{p.title}</h3></div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default function ProjectDetail() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <Analytics />
        <ProjectBody />
      </ReactLenis>
    </ContentProvider>
  );
}
